// ============================================================================
// lib/checkout/web.ts — wrapper Stripe pour le checkout web (existing).
// ============================================================================
// Réutilise l'edge fn `create-pass-checkout` déjà en prod. Sur `success`,
// Stripe redirige la page vers son URL de checkout — le retour va sur
// `?pass_success=1` qui invalide les caches côté client.
// ============================================================================

import { supabase } from '@/lib/supabase';
import type { CheckoutProductId, PurchaseResult } from './types';

/**
 * Mapping product interne → paramètres `create-pass-checkout` (Stripe web).
 * Miroir des 8 products IAP côté natif : les 6 subs auto-renouvelables (2
 * mensuels + 4 passes durée fixe) et les 2 consumables 24h se routent tous
 * vers le passe Stripe équivalent existant (`type` × `duration`).
 * Les abonnements `student_monthly` / `family_monthly` mappent sur `30d`
 * en attendant qu'on crée de vrais products Stripe récurrents dédiés.
 */
const WEB_ROUTING: Record<
  Exclude<CheckoutProductId, 'jeraisonne_tutor_monthly'>,
  { type: 'normal' | 'epreuves'; duration: '24h' | '7d' | '30d' }
> = {
  jeraisonne_student_monthly: { type: 'normal', duration: '30d' },
  jeraisonne_family_monthly: { type: 'normal', duration: '30d' },
  jeraisonne_pass_normal_24h: { type: 'normal', duration: '24h' },
  jeraisonne_pass_normal_7d: { type: 'normal', duration: '7d' },
  jeraisonne_pass_normal_30d: { type: 'normal', duration: '30d' },
  jeraisonne_pass_epreuves_24h: { type: 'epreuves', duration: '24h' },
  jeraisonne_pass_epreuves_7d: { type: 'epreuves', duration: '7d' },
  jeraisonne_pass_epreuves_30d: { type: 'epreuves', duration: '30d' },
};

export async function purchaseWeb(productId: CheckoutProductId): Promise<PurchaseResult> {
  if (productId === 'jeraisonne_tutor_monthly') return purchaseTutorWeb();
  const routing = WEB_ROUTING[productId];
  if (!routing) return { status: 'error', message: `no_web_routing:${productId}` };

  const { data, error } = await supabase.functions.invoke('create-pass-checkout', {
    body: { ...routing, return_to: window.location.href },
  });
  if (error || !data?.url) {
    return {
      status: 'error',
      message: (error as { message?: string })?.message ?? 'checkout_failed',
    };
  }
  // Redirection côté Stripe — la Promise ne résout pas vraiment (navigation).
  window.location.href = data.url as string;
  return { status: 'pending' };
}

/**
 * Abonnement Prof (migration 0691) : vrai abonnement Stripe récurrent (prix
 * `jeraisonne_tutor_monthly_v1`), géré ensuite dans le portail Stripe. Le
 * retour arrive sur `?sub_success=1`.
 */
async function purchaseTutorWeb(): Promise<PurchaseResult> {
  const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
    body: { plan: 'tutor_monthly', return_to: window.location.href },
  });
  if (error || !data?.url) {
    return {
      status: 'error',
      message:
        (data as { error?: string } | null)?.error ??
        (error as { message?: string })?.message ??
        'checkout_failed',
    };
  }
  window.location.href = data.url as string;
  return { status: 'pending' };
}
